import type React from 'react'
import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { api } from './api'
import { useAuth } from './auth'
import type { Farm, FarmAreaSummary } from './types'

/** What the farm form sends: the farm's name, address and location pin. Everything but the name is optional. */
export type FarmInput = {
  name?: string
  timezone?: string
  addressLine1?: string | null
  addressLine2?: string | null
  city?: string | null
  province?: string
  postalCode?: string | null
  country?: string
  latitude?: number | null
  longitude?: number | null
}

type FarmsContextValue = {
  farms: Farm[]
  summaryByFarm: Record<string, FarmAreaSummary[]>
  loading: boolean
  error: string | null
  getFarm: (farmId: string) => Farm | undefined
  addFarm: (farm: FarmInput & { name: string }) => Promise<void>
  updateFarm: (farmId: string, patch: FarmInput) => Promise<void>
}

const FarmsContext = createContext<FarmsContextValue | null>(null)

export function FarmsProvider({ children }: React.PropsWithChildren) {
  const { org } = useAuth()
  const [farms, setFarms] = useState<Farm[]>([])
  const [summary, setSummary] = useState<FarmAreaSummary[]>([])
  const [fetching, setFetching] = useState(false)
  // The org the current farm list belongs to. Until it matches the signed-in org the list is not
  // loaded yet, even before the fetch below starts: pages render (and would redirect from a farm
  // they can't find) before this provider's effect runs on a refresh or a direct link.
  const [loadedOrgId, setLoadedOrgId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const loading = fetching || (!!org && loadedOrgId !== org.id)

  const refresh = useCallback(async () => {
    if (!org) {
      setFarms([])
      setSummary([])
      setLoadedOrgId(null)
      return
    }
    setFetching(true)
    setError(null)
    try {
      const [farmList, summaryList] = await Promise.all([
        api.get<Farm[]>(`/orgs/${org.id}/farms`),
        api.get<FarmAreaSummary[]>(`/orgs/${org.id}/fields/summary`),
      ])
      setFarms(farmList)
      setSummary(summaryList)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load farms.')
    } finally {
      setLoadedOrgId(org.id)
      setFetching(false)
    }
  }, [org])

  useEffect(() => {
    refresh()
  }, [refresh])

  function getFarm(farmId: string) {
    return farms.find((farm) => farm.id === farmId)
  }

  async function addFarm(farm: FarmInput & { name: string }) {
    if (!org) return
    const created = await api.post<Farm>(`/orgs/${org.id}/farms`, farm)
    setFarms((prev) => [...prev, created])
  }

  async function updateFarm(farmId: string, patch: FarmInput) {
    if (!org) return
    const updated = await api.patch<Farm>(`/orgs/${org.id}/farms/${farmId}`, patch)
    setFarms((prev) => prev.map((farm) => (farm.id === farmId ? updated : farm)))
  }

  const summaryByFarm = summary.reduce<Record<string, FarmAreaSummary[]>>((acc, row) => {
    ;(acc[row.farmId] ??= []).push(row)
    return acc
  }, {})

  return (
    <FarmsContext.Provider value={{ farms, summaryByFarm, loading, error, getFarm, addFarm, updateFarm }}>{children}</FarmsContext.Provider>
  )
}

export function useFarms() {
  const ctx = useContext(FarmsContext)
  if (!ctx) throw new Error('useFarms must be used within a FarmsProvider')
  return ctx
}
